package games.polyclub.website.frontend.store.context

import games.polyclub.frontend.lib.messagerenderer.Viewer
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.message.Pronoun
import games.polyclub.message.PronounDetector
import games.polyclub.service.player.id.api.MojangPlayerId
import games.polyclub.tool.endpoint.GMson
import games.polyclub.tool.functional.Option
import games.polyclub.tool.strings.id.UniqueId
import games.polyclub.website.frontend.store.hooks.useAuth
import js.core.jso
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import react.*
import web.storage.localStorage

external interface UserPreferencesContextData {
    var preferences: UserPreferences
    var setPreferences: (UserPreferences) -> Unit
    var changePreferences: ((UserPreferences) -> UserPreferences) -> Unit
}

@Serializable
data class UserPreferences(
    val darkMode: Boolean = false,
    var pronoun: Option<Pronoun>? = null
)

val UserPreferencesContext = createContext<UserPreferencesContextData>()

val UserPreferencesProvider = SFC<PropsWithChildren> { props ->
    val authCtx = useAuth()
    val (preferences, setPreferencesState) = useState(try {
        localStorage.getItem("userPreferences")?.let {
            GMson.decodeFromString<Map<UniqueId, UserPreferences>>(it)
        } ?: emptyMap()
    } catch (e: Exception) { e.printStackTrace(); emptyMap() })

    val currentAuth = useCallback(authCtx.auth) {
        authCtx.auth
    }

    val userPrefs = useCallback(authCtx.auth, preferences) {
        val id = authCtx.auth?.profile?.playerId?.networkId?.uuid ?: UniqueId.ZERO
        var pref = preferences[id] ?: UserPreferences()
        if (id == UniqueId.ZERO) {
            pref = pref.copy(pronoun = preferences.values.firstOrNull { it.pronoun != null }?.pronoun)
        }
        pref
    }

    val setPreferences = useCallback(authCtx.auth, preferences) { userPreferences: UserPreferences ->
        val netId = authCtx.auth?.profile?.playerId?.networkId?.uuid ?: UniqueId.ZERO
        val updated = preferences + (netId to userPreferences)
        localStorage.setItem("userPreferences", GMson.encodeToString(updated))
        setPreferencesState(updated)
    }

    useEffect(authCtx.auth) {
        setPreferences(userPrefs().copy(pronoun = authCtx.auth?.profile?.preferredPronoun))
    }

    PronounDetector.register {
        (it as? Viewer)?.let { _->
            console.log("Prefs:", userPrefs().toString())
            userPrefs().pronoun?.getOrNull()
        }
    }
    PronounDetector.register {
        (it as? UniqueId)?.let { id ->
            val auth = currentAuth() ?: return@let null
            if (auth.profile.playerId?.networkId?.uuid == id || (auth.profile.playerId as? MojangPlayerId)?.mojangId == id) {
                userPrefs().pronoun?.getOrNull()
            } else {
                null
            }
        }
    }
    PronounDetector.register {
        (it as? String)?.let { name ->
            val auth = currentAuth() ?: return@let null
            if (auth.profile.currentName.name.equals(name, ignoreCase = true)) {
                userPrefs().pronoun?.getOrNull()
            } else {
                null
            }
        }
    }

    UserPreferencesContext.Provider {
        value = jso {
            this.preferences = userPrefs()
            this.setPreferences = setPreferences
            this.changePreferences = { updater ->
                setPreferences(updater(userPrefs()))
            }
        }
        +props.children
    }
}
